import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	Controller,
	type DefaultValues,
	type FieldError,
	useForm,
	useWatch
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { FloatingInput } from '@/components/commons/floating-input'
import { Radio } from '@/components/commons/radio'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import { AlertFillCircleIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import {
	TeamMemberFormSchema,
	type TeamMemberFormValues
} from '@/features/marquee/form-schemas'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	handleOperatorError,
	type OperatorErrorCode,
	operatorErrorCode,
	operatorErrorLabelKey,
	operatorRoleToHintKey,
	operatorRoleToLabelKey,
	operatorValidationErrors,
	splitOperatorName,
	teamFirstName
} from '@/features/marquee/utils'
import {
	createOperator,
	type MarqueeManagedOperator,
	type MarqueeOperatorUpdate,
	sdkSchemas,
	updateOperator
} from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

/**
 * The roles a person can be given here, in the SDK's own order. The request
 * enum, not `MarqueeOperatorRole`: `admin` is granted by developers only and
 * must never be offered.
 */
const ROLE_OPTIONS = sdkSchemas.zMarqueeAssignableOperatorRole.options

export type TeamMemberDialogTarget =
	| { mode: 'create' }
	| { mode: 'edit'; member: MarqueeManagedOperator }

type FormField = keyof TeamMemberFormValues

/** Request field → form field, for the messages of a 422 `validation-error`. */
const REQUEST_FIELD_TO_FORM_FIELD: Record<string, FormField> = {
	email: 'email',
	first_name: 'firstName',
	last_name: 'lastName',
	role: 'role',
	is_market_manager: 'isMarketManager',
	sf_user_id: 'sfUserId'
}

/**
 * The conflicts the person filling the form can act on, shown on the field
 * they are about rather than in a toast. Every other code is a toast.
 */
const ERROR_CODE_TO_FORM_FIELD: Partial<Record<OperatorErrorCode, FormField>> =
	{
		'operator-exists': 'email',
		'operator-deactivated': 'email',
		'operator-record-removed': 'email',
		'reviewer-removed': 'email',
		'reviewer-ambiguous': 'email',
		'domain-not-allowed': 'email',
		'market-manager-profile-removed': 'sfUserId'
	}

/**
 * No role is preselected on add, as in the design: picking one is a decision
 * about someone's access, so the form waits for it.
 */
const EMPTY: DefaultValues<TeamMemberFormValues> = {
	firstName: '',
	lastName: '',
	email: '',
	isMarketManager: false,
	sfUserId: ''
}

/**
 * An admin row never opens this form (no actions on admins), so its role is
 * not one of the options; it falls back to the lesser role rather than leaving
 * the radio group empty.
 */
const toFormValues = (
	member: MarqueeManagedOperator
): TeamMemberFormValues => ({
	...splitOperatorName(member.name),
	email: member.email,
	role: member.role === 'admin' ? 'facilitator' : member.role,
	isMarketManager: member.is_market_manager,
	sfUserId: member.sf_user_id ?? ''
})

/**
 * The PATCH body: only what changed, as the API asks. Names travel together
 * whenever either moved.
 *
 * The SF User id is sent only while the switch is on and the id changed. With
 * the switch off it is left out, never cleared: the backend keeps the stored
 * id for when the person is a Market Manager again, and a null would erase it.
 * Whatever was typed and then hidden again is therefore not a change, so a
 * form that ends where it started yields an empty body and Save stays
 * disabled.
 */
const changedFields = (
	member: MarqueeManagedOperator,
	values: TeamMemberFormValues
): MarqueeOperatorUpdate => {
	const before = toFormValues(member)
	const body: MarqueeOperatorUpdate = {}
	if (values.role !== before.role) {
		body.role = values.role
	}
	if (values.isMarketManager !== before.isMarketManager) {
		body.is_market_manager = values.isMarketManager
	}
	if (values.isMarketManager && values.sfUserId !== before.sfUserId) {
		body.sf_user_id = values.sfUserId
	}
	if (
		values.firstName !== before.firstName ||
		values.lastName !== before.lastName
	) {
		body.first_name = values.firstName
		body.last_name = values.lastName
	}
	return body
}

interface TeamMemberDialogProps {
	target: TeamMemberDialogTarget
	onClose: () => void
	/**
	 * Edit only: hands the person to the page's deactivate confirmation. Left
	 * out when they cannot be deactivated (already deactivated, or the viewer
	 * lacks the permission), which hides the button.
	 */
	onDeactivate?: () => void
}

/**
 * Add / edit person form (`createOperator` / `updateOperator`), one dialog for
 * both modes. The email is the person's identity: typed on add, read-only on
 * edit, since the API cannot change it.
 *
 * The SF User ID belongs to the market-manager switch: shown and required
 * while it is on. Switching it off on a Market Manager is allowed but warned
 * about, since events that list them keep doing so and new Market Visits
 * export without their Salesforce attribution.
 *
 * Controlled by the page and mounted only while open, keyed on the target, so
 * every open starts from the row it edits (or a blank form) without a reset
 * effect. Save stays disabled on an untouched edit.
 *
 * Both writes refetch the roster and the market-manager picker, so event forms
 * see the change. The add result is a toast worded by `account` (created or
 * linked); the staging activation link is not shown.
 *
 * Desktop → `Dialog`, mobile → `Drawer`; the form body is shared.
 */
function TeamMemberDialog({
	target,
	onClose,
	onDeactivate
}: TeamMemberDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const queryClient = useQueryClient()
	const member = target.mode === 'edit' ? target.member : undefined

	const {
		register,
		control,
		handleSubmit,
		setError,
		watch,
		formState: { errors, isValid }
	} = useForm<TeamMemberFormValues>({
		resolver: zodResolver(TeamMemberFormSchema),
		defaultValues: member ? toFormValues(member) : EMPTY
	})
	const isMarketManager = useWatch({ control, name: 'isMarketManager' })

	// Save on edit follows what would actually be sent, not `isDirty`: typing
	// an SF id and switching Market Manager back off dirties the form while
	// changing nothing, and the API refuses an empty PATCH (422 on `body`).
	const hasChanges =
		member !== undefined &&
		Object.keys(changedFields(member, watch())).length > 0

	// Only worth saying when events actually list them: with none, switching
	// the flag off changes no export.
	const showUnassignWarning =
		member?.is_market_manager === true &&
		!isMarketManager &&
		member.upcoming_assigned_events > 0

	const save = useMutation({
		mutationFn: async (values: TeamMemberFormValues) => {
			if (member) {
				await updateOperator({
					path: { operatorId: member.id },
					body: changedFields(member, values)
				})
				return undefined
			}
			const response = await createOperator({
				body: {
					email: values.email,
					first_name: values.firstName,
					last_name: values.lastName,
					role: values.role,
					is_market_manager: values.isMarketManager,
					sf_user_id: values.isMarketManager ? values.sfUserId : null
				}
			})
			return response.data
		},
		onSuccess: async (created, values) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.roster.all()
				}),
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.marquee.marketManagers.all()
				})
			])
			if (created) {
				// `created`: an activation email went out; `linked`: they
				// already had a Luminate account and simply sign in. The
				// staging-only `activation_link` is not shown.
				toast.success(
					created.account === 'created'
						? t('marquee:team_added_created_toast')
						: t('marquee:team_added_linked_toast')
				)
			} else {
				toast.success(
					t('marquee:team_updated_toast', {
						name: `${values.firstName} ${values.lastName}`
					})
				)
			}
			onClose()
		},
		onError: (error) => {
			const fieldMessages = operatorValidationErrors(error)
			if (fieldMessages) {
				let shownOnField = false
				for (const [requestField, messages] of Object.entries(
					fieldMessages
				)) {
					const field = REQUEST_FIELD_TO_FORM_FIELD[requestField]
					if (field && messages[0]) {
						// The backend's own sentence, shown as is.
						setError(field, {
							type: 'server',
							message: messages[0]
						})
						shownOnField = true
					}
				}
				// A message with no field here (`body`, an edit that sent
				// nothing) still has to be said somewhere.
				if (!shownOnField) {
					toast.error(
						Object.values(fieldMessages).flat()[0] ??
							t('errors:default')
					)
				}
				return
			}
			const code = operatorErrorCode(error)
			const field = code && ERROR_CODE_TO_FORM_FIELD[code]
			// The email is read-only on edit, so a code about it (an edit of
			// the SF id can answer `reviewer-removed`) is a toast there.
			if (code && field && !(member && field === 'email')) {
				setError(field, {
					type: 'code',
					message: operatorErrorLabelKey(code)
				})
				return
			}
			handleOperatorError(error)
		}
	})

	const onSubmit = handleSubmit((values) => save.mutate(values))

	/** Zod and code errors carry a key; a server field message is already text. */
	const errorText = (error?: FieldError) => {
		if (!error?.message) {
			return undefined
		}
		return error.type === 'server'
			? error.message
			: t(error.message as TKey)
	}

	const title = member
		? t('marquee:team_edit_title')
		: t('marquee:team_add_title')

	const fields = (
		<div className='flex flex-col gap-8'>
			<div className='grid l:grid-cols-2 gap-x-4 gap-y-8'>
				<div className='flex flex-col gap-3'>
					<Label htmlFor='firstName'>
						{t('marquee:team_first_name_label')}
					</Label>
					<FloatingInput
						errorMessage={errorText(errors.firstName)}
						inputSize='lg'
						label={t('marquee:team_first_name_placeholder')}
						type='text'
						{...register('firstName')}
					/>
				</div>
				<div className='flex flex-col gap-3'>
					<Label htmlFor='lastName'>
						{t('marquee:team_last_name_label')}
					</Label>
					<FloatingInput
						errorMessage={errorText(errors.lastName)}
						inputSize='lg'
						label={t('marquee:team_last_name_placeholder')}
						type='text'
						{...register('lastName')}
					/>
				</div>
			</div>
			<div className='flex flex-col gap-3'>
				<Label htmlFor='email'>{t('marquee:team_email_label')}</Label>
				<FloatingInput
					errorMessage={errorText(errors.email)}
					helpText={t('marquee:team_email_hint')}
					inputSize='lg'
					label={t('marquee:team_email_placeholder')}
					readOnly={member !== undefined}
					type='email'
					{...register('email')}
				/>
			</div>
			<Controller
				control={control}
				name='role'
				render={({ field }) => (
					<div className='flex flex-col gap-3'>
						<Label>{t('marquee:team_col_role')}</Label>
						<div className='flex flex-col gap-3'>
							{ROLE_OPTIONS.map((option) => (
								<Radio
									checked={field.value === option}
									key={option}
									label={
										<span className='flex flex-col'>
											<span className='font-medium text-base text-neutral-900'>
												{t(
													operatorRoleToLabelKey[
														option
													]
												)}
											</span>
											<span className='text-neutral-500 text-sm'>
												{t(
													operatorRoleToHintKey[
														option
													]
												)}
											</span>
										</span>
									}
									name={field.name}
									onBlur={field.onBlur}
									onChange={() => field.onChange(option)}
									ref={
										field.value === option
											? field.ref
											: undefined
									}
									value={option}
								/>
							))}
						</div>
						<InputHelpText error={errorText(errors.role)} />
					</div>
				)}
			/>
			<Controller
				control={control}
				name='isMarketManager'
				render={({ field }) => (
					<div className='flex flex-col gap-3'>
						<Label>
							{t('marquee:team_market_manager_status_label')}
						</Label>
						<div className='flex items-center justify-between gap-4 rounded-2xl bg-grey-100 px-4 py-3'>
							<div className='flex flex-col'>
								<span className='font-medium text-base text-neutral-900'>
									{t(
										'marquee:team_market_manager_toggle_title'
									)}
								</span>
								<span className='text-neutral-500 text-sm'>
									{t(
										'marquee:team_market_manager_toggle_hint'
									)}
								</span>
							</div>
							<Switch
								checked={field.value}
								onChange={field.onChange}
							/>
						</div>
						{showUnassignWarning && member && (
							<p className='flex items-start gap-2 text-red-700 text-sm'>
								<AlertFillCircleIcon
									className='mt-0.5 shrink-0'
									size={16}
								/>
								{t('marquee:team_market_manager_off_warning', {
									firstName: teamFirstName(member),
									count: member.upcoming_assigned_events
								})}
							</p>
						)}
					</div>
				)}
			/>
			{isMarketManager && (
				<div className='flex flex-col gap-3'>
					<Label htmlFor='sfUserId'>
						{t('marquee:team_sf_user_id_label')}
					</Label>
					<FloatingInput
						errorMessage={errorText(errors.sfUserId)}
						helpText={t('marquee:team_sf_user_id_hint')}
						inputSize='lg'
						label={t('marquee:team_sf_user_id_placeholder')}
						type='text'
						{...register('sfUserId')}
					/>
				</div>
			)}
		</div>
	)

	const actions = (
		<>
			<Button onClick={onClose} type='button' variant='tertiary'>
				{t('marquee:cancel')}
			</Button>
			<div className='flex items-center gap-3'>
				{member && onDeactivate && (
					<Button
						onClick={onDeactivate}
						type='button'
						variant='secondaryOutline'
					>
						{t('marquee:team_action_deactivate')}
					</Button>
				)}
				<Button
					// Add waits for a complete form, edit for an actual change,
					// both as in the design.
					disabled={
						save.isPending || (member ? !hasChanges : !isValid)
					}
					onClick={onSubmit}
					type='button'
					variant='primary'
				>
					{member
						? t('marquee:team_save')
						: t('marquee:team_send_invite')}
					{save.isPending && <Spinner size={16} />}
				</Button>
			</div>
		</>
	)

	const onOpenChange = (open: boolean) => {
		if (!open) {
			onClose()
		}
	}

	if (isDesktop) {
		return (
			<Dialog onOpenChange={onOpenChange} open>
				{/* The body scrolls and the footer stays put: `DialogContent` is a
				    grid capped at the viewport, so its scrolling row needs an
				    explicit `minmax(0,1fr)`. */}
				<DialogContent className='max-w-140 grid-rows-[minmax(0,1fr)_auto]'>
					<DialogBody className='gap-8'>
						<DialogTitle className='text-center'>
							{title}
						</DialogTitle>
						{fields}
					</DialogBody>
					<DialogFooter className='flex flex-row justify-between'>
						{actions}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={onOpenChange} open>
			<DrawerContent>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title}
					</DrawerTitle>
				</DrawerHeader>
				{/* The drawer is capped at 80vh, so the fields take the leftover
				    space and scroll rather than pushing the footer off-screen. */}
				<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-1'>
					{fields}
				</div>
				<DrawerFooter className='flex-row justify-between'>
					{actions}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export { TeamMemberDialog }
